import { useTranslation } from "@coworker/locales";
import { useMemo } from "react";
import { FixaListView } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListView";
import { FixaListViewItem } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListViewItem";
import { DatePicker } from "@coworker/apprestructured/src/shared/components/DatePicker/DatePicker";
import dayjs from "dayjs";
import {
  calcIsFifthWeekdayOfMonth,
  calcIsLastWeekdayOfMonth,
  calculateWeekdayOrdinalTranslationKey,
  getWeekdayNumber,
  getWeekdays,
} from "@coworker/apprestructured/src/task-management/components/ScheduleTask/helper";
import { MonthlyRecurrenceType } from "@coworker/apprestructured/src/task-management/types/Schedule";

type MonthlyProps = {
  start: string | null;
  end: string | null;
  startDateError: string | null;
  endDateError: string | null;
  type: MonthlyRecurrenceType;
  onStartChange: (start: string | null) => void;
  onEndChange: (end: string | null) => void;
  onTypeChange: (option: MonthlyRecurrenceType) => void;
};

export const Monthly = (props: MonthlyProps) => {
  const { t } = useTranslation();

  const today = useMemo(() => dayjs().format("YYYY-MM-DD"), []);

  const isLastWeekday = useMemo(() => {
    return props.start ? calcIsLastWeekdayOfMonth(props.start) : false;
  }, [props.start]);

  const isFifthWeekday = useMemo(() => {
    return props.start ? calcIsFifthWeekdayOfMonth(props.start) : false;
  }, [props.start]);

  const startWeekdayNumber = useMemo(
    () => (props.start ? getWeekdayNumber(props.start) : null),
    [props.start]
  );
  const weekdays = useMemo(() => getWeekdays(t), [t]);
  const startDateWeekday = useMemo(
    () => weekdays.find((day) => day.weekdayNumber === startWeekdayNumber),
    [startWeekdayNumber, weekdays]
  );

  return (
    <>
      <DatePicker
        label={t("startDateString")}
        value={props.start}
        onChange={(value) => props.onStartChange(value)}
        errorMessage={props.startDateError}
        min={today}
      />
      {props.start && (
        <FixaListView id="monthlyOptions" size="small">
          {!isFifthWeekday && (
            <FixaListViewItem
              title={t("repeatOnOfMonthString", {
                value: props.start
                  ? `${t(calculateWeekdayOrdinalTranslationKey(props.start))} ${startDateWeekday?.weekday}`
                  : "-",
              })}
              control="radiobutton"
              onChange={() => {
                props.onTypeChange("weekday");
              }}
              controlProps={{
                checked: props.type === "weekday",
                value: "weekday",
              }}
            />
          )}
          {isLastWeekday && (
            <FixaListViewItem
              title={t("repeatOnOfMonthString", {
                value: `${t("lastString")} ${startDateWeekday?.weekday}`,
              })}
              control="radiobutton"
              onChange={() => {
                props.onTypeChange("last_weekday");
              }}
              controlProps={{
                checked: props.type === "last_weekday",
                value: "last_weekday",
              }}
            />
          )}
        </FixaListView>
      )}
      <DatePicker
        label={t("endDateString")}
        value={props.end}
        onChange={(value) => props.onEndChange(value)}
        errorMessage={props.endDateError}
        min={today}
      />
    </>
  );
};
